/**
 * Notifications Service
 * =====================
 * Client-side service for reading and managing in-app notifications.
 * Notifications are WRITTEN by Cloud Functions only — client reads + marks read.
 */

import {
  collection,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Notification, ApiResult } from "@/types";

const NOTIFS = "notifications";

function docToNotif(id: string, data: Record<string, unknown>): Notification {
  return {
    id,
    userId:    String(data["userId"]    ?? ""),
    type:      (data["type"] as Notification["type"]),
    title:     String(data["title"]     ?? ""),
    body:      String(data["body"]      ?? ""),
    bookId:    data["bookId"]    as string | undefined,
    requestId: data["requestId"] as string | undefined,
    isRead:    Boolean(data["isRead"]),
    createdAt: (data["createdAt"] as { toDate: () => Date } | null)?.toDate() ?? new Date(),
  };
}

// ─── FETCH NOTIFICATIONS ─────────────────────────────────────────────────────
export async function fetchNotifications(
  userId:    string,
  maxCount = 30
): Promise<ApiResult<Notification[]>> {
  try {
    const q = query(
      collection(db, NOTIFS),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(maxCount)
    );
    const snap = await getDocs(q);
    const notifs = snap.docs.map(d => docToNotif(d.id, d.data() as Record<string, unknown>));
    return { ok: true, data: notifs };
  } catch (err) {
    console.error("[fetchNotifications]", err);
    return { ok: false, code: "fetch-failed", message: "Failed to load notifications." };
  }
}

// ─── MARK READ ────────────────────────────────────────────────────────────────
export async function markNotificationRead(notifId: string): Promise<void> {
  try {
    await updateDoc(doc(db, NOTIFS, notifId), { isRead: true });
  } catch (err) {
    console.error("[markNotificationRead]", err);
  }
}

export async function markAllRead(userId: string): Promise<void> {
  try {
    const q    = query(collection(db, NOTIFS), where("userId", "==", userId), where("isRead", "==", false));
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(d => updateDoc(d.ref, { isRead: true })));
  } catch (err) {
    console.error("[markAllRead]", err);
  }
}

// ─── REAL-TIME UNREAD COUNT ───────────────────────────────────────────────────
export function subscribeToUnreadCount(
  userId: string,
  cb:     (count: number) => void
): Unsubscribe {
  const q = query(
    collection(db, NOTIFS),
    where("userId", "==", userId),
    where("isRead", "==", false)
  );
  return onSnapshot(q, snap => cb(snap.size));
}

// ─── REAL-TIME NOTIFICATIONS FEED ────────────────────────────────────────────
export function subscribeToNotifications(
  userId: string,
  cb:     (notifs: Notification[]) => void
): Unsubscribe {
  const q = query(
    collection(db, NOTIFS),
    where("userId", "==", userId),
    orderBy("createdAt", "desc"),
    limit(20)
  );
  return onSnapshot(q, snap => {
    cb(snap.docs.map(d => docToNotif(d.id, d.data() as Record<string, unknown>)));
  });
}
